// ==============================================================================
// FILE: src/pages/BulkJobStatusDashboardPage.tsx (Feature Complete)
// PURPOSE: Displays bulk job status and provides intelligent recovery options.
// VERSION: 4.1 - Implements "Retry All" and "Correct Files" workflows.
// ==============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Accordion,
  AccordionSummary, AccordionDetails, Chip, LinearProgress, Tooltip,
  IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, CheckCircle as CheckCircleIcon,
  Error as ErrorIcon, HourglassTop as HourglassTopIcon, Sync as SyncIcon,
  FactCheck as ValidationIcon, Replay as ReplayIcon, CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAppContext } from '../contexts/AppContext';
import { BulkIngestJob, BulkJobClaim, BatchStatus } from '../types';
import FileUploader from '../components/FileUploader'; // Import the uploader

const getStatusChipProps = (status: BatchStatus): { color: 'success' | 'error' | 'warning' | 'info' | 'default', icon: React.ReactElement } => {
    switch (status) {
        case 'completed':
        case 'review_required':
            return { color: 'success', icon: <CheckCircleIcon /> };
        case 'error':
            return { color: 'error', icon: <ErrorIcon /> };
        case 'pending_validation':
            return { color: 'info', icon: <ValidationIcon /> };
        case 'processing':
        case 'pending_files':
            return { color: 'info', icon: <HourglassTopIcon /> };
        default:
            return { color: 'default', icon: <HourglassTopIcon /> };
    }
};

const JobDetailsGrid = ({ claims, onCorrectFilesClick }: { claims: BulkJobClaim[], onCorrectFilesClick: (claim: BulkJobClaim) => void }) => {
  const { setActivePage, setReviewingBatchId } = useAppContext();

  const handleStatusClick = (claim: BulkJobClaim) => {
    if (claim.status === 'review_required' && claim.ingestion_batch_id) {
      setReviewingBatchId(claim.ingestion_batch_id);
      setActivePage('Claim Review');
    }
    // You can add navigation for other statuses here if needed
  };

  const columns: GridColDef[] = [
    { field: 'employee_folder_name', headerName: 'Employee Folder', flex: 1, minWidth: 200 },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      renderCell: (params: GridRenderCellParams<BulkJobClaim>) => {
        const { color, icon } = getStatusChipProps(params.row.status);
        const isClickable = params.row.status === 'review_required';
        return (
          <Tooltip title={isClickable ? "Click to review this claim" : ""}>
            <Chip 
              icon={icon} 
              label={params.row.status.replace(/_/g, ' ')} 
              color={color} 
              size="small" 
              variant="outlined" 
              onClick={isClickable ? () => handleStatusClick(params.row) : undefined}
              sx={{ cursor: isClickable ? 'pointer' : 'default' }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'error_message',
      headerName: 'Details',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" noWrap sx={{ color: params.row.status === 'error' ? 'error.main' : 'text.primary' }}>
            {params.value || '---'}
          </Typography>
        </Tooltip>
      ),
    },
    {
        field: 'actions',
        headerName: 'Actions',
        width: 150,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<BulkJobClaim>) => {
            if (params.row.status === 'error') {
                return (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onCorrectFilesClick(params.row)}
                    >
                        Correct Files
                    </Button>
                );
            }
            return null;
        }
    }
  ];

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <DataGrid rows={claims} columns={columns} getRowId={(row) => row.id} density="compact" />
    </Box>
  );
};

const JobAccordion = ({ jobData, expanded, onChange }: { jobData: BulkIngestJob, expanded: boolean, onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void }) => {
  const { apiService, showNotification } = useAppContext();
  const [details, setDetails] = useState<BulkJobClaim[] | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const [openReplaceDialog, setOpenReplaceDialog] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<BulkJobClaim | null>(null);
  const [newClaimSheet, setNewClaimSheet] = useState<File[]>([]);
  const [newReceipts, setNewReceipts] = useState<File[]>([]);
  const [isReplacing, setIsReplacing] = useState(false);

  const fetchDetails = useCallback(async () => {
    setIsLoadingDetails(true);
    try {
      const detailedJob = await apiService.getBulkJobDetails(jobData.id);
      setDetails(detailedJob.claims);
    } catch (err: any) {
      showNotification(err.message || 'Failed to fetch job details.', 'error');
    } finally {
      setIsLoadingDetails(false);
    }
  }, [apiService, showNotification, jobData.id]);
  
  // --- THIS IS THE FIX (Part 1) ---
  // This effect ensures that if the accordion is open and the parent component
  // provides updated job data (e.g., from polling), the details are re-fetched.
  useEffect(() => {
    // If the accordion is expanded and the job is still actively processing, refresh its details.
    // if (expanded && jobData.status === 'processing') {
    //   fetchDetails();
    // }
    // Fetch details on the initial expansion as well.
    if (expanded && !details) {
      fetchDetails();
    }
  // }, [jobData, expanded, details, fetchDetails]);
  }, [expanded, details, fetchDetails]);
  // --- END OF FIX ---

  const handleRetryAllFailed = async (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsRetrying(true);
      try {
          const response = await apiService.retryFailedBulkJob(jobData.id);
          showNotification(response.message || 'Retry request sent for all failed claims!', 'success');
          setTimeout(fetchDetails, 2000);
      } catch(err: any) {
          showNotification(err.message || 'Failed to retry job.', 'error');
      } finally {
        setIsRetrying(false);
      }
  };

  const handleOpenCorrectFilesModal = (claim: BulkJobClaim) => {
      setSelectedClaim(claim);
      setNewClaimSheet([]);
      setNewReceipts([]);
      setOpenReplaceDialog(true);
  };

  const handleReplaceAndRetry = async () => {
      if (!selectedClaim || newClaimSheet.length === 0) {
          showNotification('A new claim sheet is required.', 'warning');
          return;
      }
      setIsReplacing(true);
      try {
          await apiService.replaceAndRetryBulkClaim(selectedClaim.id, newClaimSheet, newReceipts);
          showNotification('Files replaced and claim re-queued!', 'success');
          setOpenReplaceDialog(false);
          setTimeout(fetchDetails, 2000); 
      } catch (err: any) {
          showNotification(err.message || 'Failed to replace files and retry.', 'error');
      } finally {
          setIsReplacing(false);
      }
  };

  const getJobStatusIcon = (status: 'processing' | 'completed' | 'failed') => {
    if (status === 'completed') return <CheckCircleIcon color="success" />;
    if (status === 'failed') return <ErrorIcon color="error" />;
    return <HourglassTopIcon color="info" />;
  };
  
  const hasFailedClaims = details?.some(c => c.status === 'error');

  return (
    <>
        <Accordion expanded={expanded} onChange={onChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                {getJobStatusIcon(jobData.status)}
                <Typography sx={{ flexBasis: '40%', flexShrink: 0, fontWeight: 500 }}>
                    {jobData.original_zip_file_name}
                </Typography>
                <Typography sx={{ flexBasis: '20%', color: 'text.secondary' }}>
                    {jobData.total_claims_detected} Claims
                </Typography>
                <Box sx={{ width: '100%', flexBasis: '40%' }}>
                    <LinearProgress variant="determinate" value={jobData.progress} />
                </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {isLoadingDetails && <CircularProgress />}
                {!isLoadingDetails && details && (
                    <>
                        {hasFailedClaims && (
                            <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-end'}}>
                                <Button 
                                    variant="contained" 
                                    startIcon={isRetrying ? <CircularProgress size={20} color="inherit" /> : <ReplayIcon />}
                                    onClick={handleRetryAllFailed}
                                    disabled={isRetrying}
                                >
                                    Retry All System Failures
                                </Button>
                            </Box>
                        )}
                        <JobDetailsGrid claims={details} onCorrectFilesClick={handleOpenCorrectFilesModal} />
                    </>
                )}
            </AccordionDetails>
        </Accordion>

        <Dialog open={openReplaceDialog} onClose={() => setOpenReplaceDialog(false)} fullWidth maxWidth="md">
            <DialogTitle>Correct Files for: {selectedClaim?.employee_folder_name}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{mt: 1}}>
                    <Grid item xs={12} md={6}>
                        <FileUploader
                            title="1. Upload New Claim Sheet"
                            description="Upload the single correct sheet (.xlsx)."
                            onFilesSelected={(files) => setNewClaimSheet(files)}
                            onFileRemove={() => setNewClaimSheet([])}
                            acceptedFileTypes={{ 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }}
                            allowMultiple={false}
                            files={newClaimSheet}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FileUploader
                            title="2. Upload New Receipts"
                            description="Upload all correct receipts (.pdf, .png, .jpg)."
                            onFilesSelected={(files) => setNewReceipts(prev => [...prev, ...files])}
                            onFileRemove={(fileToRemove) => setNewReceipts(prev => prev.filter(f => f !== fileToRemove))}
                            acceptedFileTypes={{ 'image/jpeg': [], 'image/png': [], 'application/pdf': [] }}
                            allowMultiple={true}
                            files={newReceipts}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenReplaceDialog(false)}>Cancel</Button>
                <Button onClick={handleReplaceAndRetry} variant="contained" disabled={isReplacing || newClaimSheet.length === 0}>
                    {isReplacing ? <CircularProgress size={24} /> : "Replace and Retry"}
                </Button>
            </DialogActions>
        </Dialog>
    </>
  );
};

export default function BulkJobStatusDashboardPage(): JSX.Element {
  const { apiService, showNotification } = useAppContext();
  const [jobs, setJobs] = useState<BulkIngestJob[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange = (jobId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? jobId : false);
  };

  const fetchJobs = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
        setLoading(true);
    }
    try {
      const data = await apiService.getBulkJobs();
      setJobs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load bulk job history.');
      showNotification(err.message || 'Failed to load bulk job history.', 'error');
    } finally {
      if (isManualRefresh) {
          setLoading(false);
      }
    }
  }, [apiService, showNotification]);

  useEffect(() => {
    fetchJobs(true); 
    const interval = setInterval(() => fetchJobs(false), 15000); 
    return () => clearInterval(interval);
  }, [fetchJobs]);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          Bulk Job Status
        </Typography>
        <Tooltip title="Refresh Now">
          <span>
            <IconButton onClick={() => fetchJobs(true)} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : <SyncIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      
      {!loading && !error && (
        <Box sx={{ mt: 2 }}>
          {jobs.length === 0 ? (
            <Typography>No bulk jobs found.</Typography>
          ) : (
            // --- THIS IS THE FIX (Part 2) ---
            // The parent component now manages the expansion state and passes it down
            // to each accordion, enabling the real-time refresh logic.
            jobs.map((job) => (
              <JobAccordion 
                key={job.id} 
                jobData={job} 
                expanded={expanded === job.id} 
                onChange={handleAccordionChange(job.id)}
              />
            ))
            // --- END OF FIX ---
          )}
        </Box>
      )}
    </Paper>
  );
}
